import { useAuthStore } from '@/store/authStore'
import { useLogin } from '@/hooks/mutations/useLogin'
import { useLogout } from '@/hooks/mutations/useLogout'

/**
 * Custom hook that provides a simple interface to the authentication system
 *
 * @returns {Object} Authentication state and methods
 * - isAuthenticated: boolean - Whether the user is logged in
 * - user: User | null - The current user object
 * - token: string | null - The authentication token
 * - login: function - Login mutation function
 * - logout: function - Logout mutation function
 * - isLoading: boolean - Whether a login or logout operation is in progress
 *
 * @example
 * ```tsx
 * const { isAuthenticated, user, login, logout } = useAuth()
 *
 * if (isAuthenticated) {
 *   return <div>Welcome {user?.name}</div>
 * }
 * ```
 */
export const useAuth = () => {
  const { isAuthenticated, user, token } = useAuthStore()
  const { mutate: login, isPending: isLoggingIn } = useLogin()
  const { mutate: logout, isPending: isLoggingOut } = useLogout()

  return {
    isAuthenticated,
    user,
    token,
    login,
    logout,
    isLoading: isLoggingIn || isLoggingOut,
  }
}
